import clsx from "clsx";
import React from "react";
import { Box } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";

import {
  ComponentTemplatePrivacyType,
  ComponentTemplateSort,
} from "@growth-machine-llc/stridist-api-client";
import { LibraryHeaderFilters } from "../header-filters/HeaderFilters";
import { JourneyAnchors } from "../../constants";
import ToggleIconButtonGroup from "../button/ToggleIconButtonGroup";
import ResetFiltersButton from "../exercise-library/filters/ResetFiltersButton";
import { useToastAlert } from "../app/ToastAlert/ToastAlertProvider";

const height = 40;

const useStyles = makeStyles((theme) => ({
  templateType: {
    height,
  },
  inputActive: {
    "&.MuiOutlinedInput-root": {
      "& fieldset": {
        borderColor: theme.palette.primary.main,
      },
      "&:hover fieldset": {
        borderColor: theme.palette.primary.main,
      },
    },

    "& .MuiOutlinedInput-root": {
      "& fieldset": {
        borderColor: theme.palette.primary.main,
      },
      "&:hover fieldset": {
        borderColor: theme.palette.primary.main,
      },
    },
  },

  sort: {
    backgroundColor: theme.palette.background.paper,
    fontWeight: 500,
    flexGrow: 1,
    width: "100%",
    justifyContent: "flex-end",
    [theme.breakpoints.down("sm")]: {
      justifyContent: "space-between",
    },
  },

  sortOption: {
    fontWeight: 500,
  },

  resetFilters: {
    padding: theme.spacing(1, 2),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.spacing(1),
    height: height,
  },
}));

const ComponentTemplateTypeOptions = [
  {
    value: ComponentTemplatePrivacyType.ALL,
    tooltip: "All component templates",
    label: "All",
  },
  {
    value: ComponentTemplatePrivacyType.PRIVATE,
    tooltip: "Private component templates",
    label: "Private",
  },
  {
    value: ComponentTemplatePrivacyType.TEAM,
    tooltip: "Team component templates (Coming soon)",
    label: "Team",
  },
  {
    value: ComponentTemplatePrivacyType.PUBLIC,
    tooltip: "Public component templates",
    label: "Public",
  },
];

export interface CoachComponentTemplateLibraryHeaderFiltersProps {
  query: string;
  sortKey: ComponentTemplateSort;
  onChangeQuery?: (query: string) => void;
  onChangeSortKey?: (sortKey: ComponentTemplateSort) => void;
  templatePrivacyType?: ComponentTemplatePrivacyType;
  onChangeTemplatePrivacyType?: (
    templatePrivacyType: ComponentTemplatePrivacyType,
  ) => void;
  defaultValues: {
    query: string;
    sortKey: ComponentTemplateSort;
  };
  showResetButton?: boolean;
  disabledResetButton?: boolean;
  onResetFilters?: () => void;
  className?: string;
}

export function CoachComponentTemplateLibraryHeaderFilters(
  props: CoachComponentTemplateLibraryHeaderFiltersProps,
) {
  const s = useStyles();
  const {
    className,
    query,
    onChangeQuery,
    templatePrivacyType,
    onChangeTemplatePrivacyType,
    defaultValues: { query: defaultQuery },
    showResetButton,
    disabledResetButton,
    onResetFilters,
  } = props;

  const { showToastAlert } = useToastAlert();

  const handleChangeTemplatePrivacyType = React.useCallback(
    (value: ComponentTemplatePrivacyType) => {
      //TODO_COMPONENT_TEAM_LIBRARY: Remove when team templates are available
      if (value === ComponentTemplatePrivacyType.TEAM) {
        showToastAlert("info", {
          message: "Team component templates will be available soon!",
        });
        return;
      }

      if (onChangeTemplatePrivacyType) {
        onChangeTemplatePrivacyType(value);
      }
    },
    [onChangeTemplatePrivacyType],
  );

  return (
    <>
      <LibraryHeaderFilters
        query={query}
        onChangeQuery={onChangeQuery}
        className={className}
        searchPlaceholder={`Search by name`}
        defaultValues={{
          query: defaultQuery,
        }}
        selectItem={
          <Box display="flex" alignItems="center" gap={2} className={s.sort}>
            <ToggleIconButtonGroup
              className={clsx(
                s.templateType,
                JourneyAnchors.COMPONENT_TEMPLATE_PRIVACY_FILTER,
              )}
              options={ComponentTemplateTypeOptions}
              value={templatePrivacyType}
              onChange={handleChangeTemplatePrivacyType}
              displayLabel="always"
            />

            {showResetButton && (
              <ResetFiltersButton
                disabled={disabledResetButton}
                onClick={onResetFilters}
                className={s.resetFilters}
              />
            )}
          </Box>
        }
      />
    </>
  );
}
